import { search as fuzzySearch } from '@debbie/common/dist/util/index.js';
import { autoUpdate, computePosition, flip, offset, shift, } from '@floating-ui/dom';
import React, { useEffect, useLayoutEffect, useMemo, useRef, } from 'react';
import { createPortal } from 'react-dom';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import ChevronDown from './Icons/ChevronDown.js';
import { ACCENT, Chevron, Container, EmptyRow, SearchField, SearchIcon, SearchIconWrap, Trigger, TriggerLabel, useHighlightedRow, useSearchableDropdown, } from './SearchableDropdown.js';
const isRich = (props) => Array.isArray(props.options);
const Select = (props) => {
    if (isRich(props)) {
        return React.createElement(RichSelect, { ...props });
    }
    return React.createElement(NativeSelect, { ...props }, props.children);
};
export default Select;
const NativeSelect = (props) => {
    return (React.createElement("label", null,
        props.label != null ? React.createElement("span", null, props.label) : null,
        React.createElement(Wrapper, { style: props.labelStyle },
            React.createElement(SelectStyled, { ...props, style: props.style }, props.children),
            React.createElement(ChevronDown, { style: {
                    pointerEvents: 'none',
                } }))));
};
const CheckIcon = () => (React.createElement("svg", { viewBox: "0 0 16 16", width: "12", height: "12", "aria-hidden": "true" },
    React.createElement("path", { d: "M13.5 4.5l-6.5 7L2.5 8", fill: "none", stroke: "currentColor", strokeWidth: "2.2", strokeLinecap: "round", strokeLinejoin: "round" })));
const RichSelect = (props) => {
    const { options, selected, onChange, label = '', title, metaLabel, searchPlaceholder = 'Type to search', searchThreshold = 0, showSelectAll = true, multi = false, disabled = false, createOption, portal = false, style, className, } = props;
    const single = !multi;
    const effectiveShowSelectAll = showSelectAll && !single;
    const panelRef = useRef(null);
    const { open, close, search, setSearch, containerProps, triggerProps, triggerRef, } = useSearchableDropdown({ disabled, panelRef });
    const { handleKeyDown: handleListKeyDown, rowProps } = useHighlightedRow(open, search);
    useLayoutEffect(() => {
        const trigger = triggerRef.current;
        const panel = panelRef.current;
        if (!open || !portal || trigger === null || panel === null) {
            return undefined;
        }
        return autoUpdate(trigger, panel, () => {
            panel.style.width = `${Math.max(trigger.offsetWidth, 240)}px`;
            panel.style.font = window.getComputedStyle(trigger).font;
            void computePosition(trigger, panel, {
                strategy: 'fixed',
                placement: 'bottom-start',
                middleware: [offset(6), flip(), shift({ padding: 8 })],
            }).then(({ x, y }) => {
                panel.style.left = `${x}px`;
                panel.style.top = `${y}px`;
            });
        });
    }, [open, portal, triggerRef]);
    // With a createOption the search input doubles as the name of the option
    // to create, so it must be available even when there are no options yet
    const showSearch = options.length > searchThreshold || createOption != null;
    // Typo-tolerant search, ranked by relevance (best match first)
    const filtered = useMemo(() => {
        if (!showSearch || search.trim() === '') {
            return options;
        }
        return fuzzySearch(options, (o) => (o.searchStr != null ? `${o.label} ${o.searchStr}` : o.label), search.trim(), {});
    }, [options, search, showSearch]);
    const selectedSet = useMemo(() => new Set(selected), [selected]);
    const toggle = (value) => {
        if (single) {
            onChange(selectedSet.has(value) ? [] : [value]);
            close();
            return;
        }
        onChange(selectedSet.has(value)
            ? selected.filter((v) => v !== value)
            : selected.concat(value));
    };
    const filteredValues = filtered.map((o) => o.value);
    const allFilteredSelected = filteredValues.length > 0 && filteredValues.every((v) => selectedSet.has(v));
    const someFilteredSelected = filteredValues.some((v) => selectedSet.has(v));
    const toggleAll = () => {
        if (allFilteredSelected) {
            const toRemove = new Set(filteredValues);
            onChange(selected.filter((v) => !toRemove.has(v)));
        }
        else {
            const merged = new Set(selected);
            filteredValues.forEach((v) => merged.add(v));
            onChange(Array.from(merged));
        }
    };
    const hasMeta = metaLabel != null || options.some((o) => o.meta !== undefined);
    // Without a search field to autofocus, focus the panel so it still gets keys.
    useEffect(() => {
        if (open && !showSearch) {
            panelRef.current?.focus();
        }
    }, [open, showSearch]);
    const create = () => {
        if (createOption) {
            createOption.onCreate(search);
            close();
        }
    };
    const showCreate = createOption !== undefined &&
        (createOption.showWhen === 'search'
            ? search.trim() !== ''
            : filtered.length === 0);
    const handleKeyDown = (e) => {
        handleListKeyDown(e, filtered.length + (showCreate ? 1 : 0), (index) => {
            if (index === filtered.length) {
                create();
            }
            else {
                toggle(filtered[index].value);
            }
        });
    };
    const panel = open ? (React.createElement(Panel, { ref: panelRef, $portal: portal, tabIndex: -1, onKeyDown: handleKeyDown, role: "listbox", "aria-multiselectable": multi },
        React.createElement(Header, null,
            effectiveShowSelectAll && (React.createElement(CheckBox, { type: "button", role: "checkbox", "aria-checked": allFilteredSelected, $checked: allFilteredSelected, $indeterminate: !allFilteredSelected && someFilteredSelected, $variant: "neutral", onClick: toggleAll }, (allFilteredSelected || someFilteredSelected) && React.createElement(CheckIcon, null))),
            React.createElement(HeaderTitle, null, title ?? label),
            hasMeta && metaLabel != null && React.createElement(HeaderMeta, null, metaLabel)),
        showSearch && (React.createElement(SearchRow, null,
            React.createElement(SearchIconWrap, null,
                React.createElement(SearchIcon, null)),
            React.createElement(SearchField, { autoFocus: true, value: search, placeholder: searchPlaceholder, onChange: (e) => setSearch(e.target.value) }))),
        React.createElement(List, null,
            filtered.length === 0 && !showCreate && React.createElement(EmptyRow, null, "\u2014"),
            filtered.map((option, index) => {
                const isSelected = selectedSet.has(option.value);
                return (React.createElement(ItemRow, { key: option.value, ...rowProps(index), role: "option", "aria-selected": isSelected, onClick: () => toggle(option.value) },
                    !single && (React.createElement(CheckBox, { as: "span", $checked: isSelected, $variant: "accent", "aria-hidden": "true" }, isSelected && React.createElement(CheckIcon, null))),
                    React.createElement(ItemText, null,
                        React.createElement(ItemLabel, { title: option.label, $selected: single && isSelected }, option.label),
                        option.description !== undefined && (React.createElement(ItemDescription, null, option.description))),
                    option.meta !== undefined && (React.createElement(ItemRight, null,
                        React.createElement(ItemMeta, null, option.meta))),
                    single && isSelected && (React.createElement(SelectedMark, null,
                        React.createElement(CheckIcon, null)))));
            }),
            showCreate && createOption !== undefined && (React.createElement(ItemRow, { ...rowProps(filtered.length), role: "option", "aria-selected": false, onClick: create },
                React.createElement(CreateIconWrap, { "aria-hidden": "true" }, "+"),
                React.createElement(ItemText, null,
                    React.createElement(ItemLabel, null, createOption.label),
                    search.trim() !== '' && (React.createElement(ItemDescription, null,
                        "\u201C",
                        search.trim(),
                        "\u201D")))))))) : null;
    return (React.createElement(Container, { ...containerProps, style: style, className: className },
        React.createElement(Trigger, { ...triggerProps },
            React.createElement(TriggerLabel, null, single && selected.length > 0
                ? (options.find((o) => o.value === selected[0])?.label ?? label)
                : label),
            !single && selected.length > 0 && (React.createElement(CountBadge, null, selected.length)),
            React.createElement(Chevron, { $open: open, "aria-hidden": "true" }, "\u25BE")),
        portal && panel !== null ? createPortal(panel, document.body) : panel));
};
const Wrapper = styled.label `
  position: relative;
  margin-bottom: 20px;
  display: block;

  & > svg {
    position: absolute;
    right: 9px;
    top: 9px;
    width: 21px;
    height: 21px;
  }
`;
const SelectStyled = styled.select `
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  border-radius: 3px;
  background-color: ${THEME.COLOR_SECONDARY};
  padding: 10px 14px 10px 15px;
  font-size: 0.9em;
  width: 100%;
  border: 1px solid ${THEME.BORDER_STRONG};
  /* box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075); */
  box-sizing: border-box;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  /* CAUTION: IE hackery ahead */
  &::-ms-expand {
    display: none; /* remove default arrow on ie10 and ie11 */
  }

  &:focus {
    border: 2px solid lightblue;
    padding: 9px 14px 9px 14px;
  }
`;
const CountBadge = styled.span `
  flex: none;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 9px;
  background: ${ACCENT};
  color: #ffffff;
  font-size: 0.72em;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
`;
const Panel = styled.div `
  position: ${({ $portal }) => ($portal ? 'fixed' : 'absolute')};
  z-index: 20;
  top: calc(100% + 6px);
  left: 0;
  width: 100%;
  min-width: 240px;
  background: #ffffff;
  border: 1px solid ${THEME.BORDER_STRONG};
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(20, 20, 40, 0.16);
  overflow: hidden;
  outline: none;
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid ${THEME.BORDER_STRONG};
  font-weight: 700;
  color: #2b2b33;
`;
const HeaderTitle = styled.span `
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const HeaderMeta = styled.span `
  flex: none;
  color: #2b2b33;
`;
const SearchRow = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 1px solid ${THEME.BORDER_STRONG};
  background: ${THEME.SURFACE_INSET};
`;
const List = styled.div `
  max-height: 320px;
  overflow-y: auto;
  padding: 4px 0;
`;
const ItemRow = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 14px;
  cursor: pointer;
  user-select: none;
  background: ${(p) => (p.$highlighted === true ? THEME.SURFACE_INSET : 'transparent')};

  &:hover {
    background: ${THEME.SURFACE_INSET};
  }
`;
const ItemText = styled.span `
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const ItemLabel = styled.span `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #2b2b33;
  font-weight: ${(p) => (p.$selected === true ? 600 : 'normal')};
`;
const SelectedMark = styled.span `
  flex: none;
  display: inline-flex;
  color: ${ACCENT};
`;
const ItemDescription = styled.span `
  font-size: 0.85em;
  line-height: 1.35;
  color: ${THEME.TEXT_MUTED};
`;
const ItemRight = styled.span `
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 56px;
`;
const ItemMeta = styled.span `
  color: ${THEME.TEXT_MUTED};
`;
const CreateIconWrap = styled.span `
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 5px;
  border: 1.5px dashed ${ACCENT};
  color: ${ACCENT};
  font-weight: 700;
  line-height: 1;
`;
const CheckBox = styled.button `
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 5px;
  border: 1.5px solid
    ${(p) => p.$checked || p.$indeterminate === true
    ? p.$variant === 'accent'
        ? ACCENT
        : '#3a3a44'
    : THEME.BORDER_STRONG};
  background: ${(p) => p.$checked || p.$indeterminate === true
    ? p.$variant === 'accent'
        ? ACCENT
        : THEME.FILL
    : '#ffffff'};
  color: ${(p) => (p.$variant === 'accent' ? '#ffffff' : '#3a3a44')};
  cursor: pointer;
  outline: none;
  transition:
    background 0.12s ease,
    border-color 0.12s ease;
`;
//# sourceMappingURL=Select.js.map